import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Fade-in animation on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Validation
  const validateField = (name, value) => {
    let error = "";

    if (name === "username") {
      if (!value.trim()) {
        error = t("forgotPassword.errors.usernameRequired");
      } else if (value.trim().length < 3) {
        error = t("forgotPassword.errors.usernameLength");
      }
    }

    if (name === "phoneNumber") {
      if (!value.trim()) {
        error = t("forgotPassword.errors.phoneRequired");
      } else if (!/^[0-9+\-\s()]+$/.test(value)) {
        error = t("forgotPassword.errors.phoneInvalid");
      } else if (value.replace(/[^0-9]/g, "").length < 8) {
        error = t("forgotPassword.errors.phoneLength");
      }
    }

    return error;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear messages when user starts typing
    if (successMessage) setSuccessMessage("");
    if (errorMessage) setErrorMessage("");

    // Only validate if field has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      username: true,
      phoneNumber: true,
    });

    // Validate all fields
    const usernameError = validateField("username", formData.username);
    const phoneError = validateField("phoneNumber", formData.phoneNumber);

    if (usernameError || phoneError) {
      setErrors({
        username: usernameError,
        phoneNumber: phoneError,
      });
      return;
    }

    // Submit request
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Replace this with your actual API call to notify the owner
      await new Promise((resolve) => setTimeout(resolve, 1500));

      console.log("Password reset request:", formData);

      // Show success message
      setSuccessMessage(t("forgotPassword.success"));

      // Reset form
      setFormData({
        username: "",
        phoneNumber: "",
      });
      setTouched({});
      setErrors({});
    } catch (error) {
      setErrorMessage(
        "Something went wrong. Please try again or contact support."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* LEFT SIDE - White form section */}
      <div
        className={`w-full lg:w-1/2 bg-white flex items-center justify-center p-6 sm:p-12 transition-all duration-1000 ${
          isVisible ? "opacity-100 -translate-x-0" : "opacity-0 -translate-x-8"
        }`}
      >
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center space-x-3 mb-12">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <img
                src="/logoBlack.png"
                alt="Logo"
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  const svg = document.createElementNS(
                    "http://www.w3.org/2000/svg",
                    "svg"
                  );
                  svg.setAttribute("class", "w-6 h-6 text-white");
                  svg.setAttribute("fill", "none");
                  svg.setAttribute("stroke", "currentColor");
                  svg.setAttribute("viewBox", "0 0 24 24");
                  svg.innerHTML =
                    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>';
                  e.target.parentElement.appendChild(svg);
                }}
              />
            </div>
            <span className="text-black text-xl font-bold">Auto Shkolla</span>
          </div>

          {/* Language Switcher - Mobile */}
          <div className="lg:hidden mb-6">
            <LanguageSwitcher />
          </div>

          {/* Back to login link */}
          <button
            onClick={() => navigate("/login")}
            className="flex items-center text-gray-600 hover:text-black transition-colors duration-200 mb-8 group"
          >
            <svg
              className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t("forgotPassword.backToLogin")}
          </button>

          {/* Header */}
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-black mb-2">
              {t("forgotPassword.title")}
            </h2>
            <p className="text-gray-600">{t("forgotPassword.subtitle")}</p>
          </div>

          {/* Success message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm animate-slide-down">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold">Request Sent!</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-black/5 border border-black/10 rounded-lg text-gray-900 text-sm animate-shake-smooth">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold">Request Failed</p>
                  <p className="text-xs text-gray-600 mt-0.5">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                {t("forgotPassword.username")}
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 bg-white border ${
                  touched.username && errors.username
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-black focus:ring-black/10"
                } rounded-lg text-gray-900 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-4`}
                placeholder={t("forgotPassword.placeholders.username")}
                aria-required="true"
                aria-invalid={errors.username ? "true" : "false"}
              />
              {touched.username && errors.username && (
                <p className="mt-2 text-xs text-gray-700 animate-slide-down">
                  {errors.username}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                {t("forgotPassword.phoneNumber")}
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 bg-white border ${
                  touched.phoneNumber && errors.phoneNumber
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-black focus:ring-black/10"
                } rounded-lg text-gray-900 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-4`}
                placeholder={t("forgotPassword.placeholders.phoneNumber")}
                aria-required="true"
                aria-invalid={errors.phoneNumber ? "true" : "false"}
              />
              {touched.phoneNumber && errors.phoneNumber && (
                <p className="mt-2 text-xs text-gray-700 animate-slide-down">
                  {errors.phoneNumber}
                </p>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-6 px-4 py-3 bg-black text-white font-medium rounded-lg transition-all duration-200 hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-black/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("forgotPassword.submitting")}
                </span>
              ) : (
                t("forgotPassword.submitButton")
              )}
            </button>
          </form>

          {/* Help text */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-900">
                {t("forgotPassword.note")}:
              </strong>{" "}
              {t("forgotPassword.noteText")}
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-600">
            {t("forgotPassword.rememberPassword")}{" "}
            <button
              onClick={() => navigate("/login")}
              className="font-medium text-black hover:underline"
            >
              {t("forgotPassword.signIn")}
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Black branding section with car */}
      <div
        className={`hidden lg:flex lg:w-1/2 bg-black relative overflow-hidden transition-all duration-1000 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        {/* Diagonal light beam effect */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 blur-3xl rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white opacity-5 blur-3xl rounded-full transform -translate-x-32 translate-y-32"></div>

        {/* Subtle road lines pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `
                repeating-linear-gradient(
                  90deg,
                  transparent,
                  transparent 50px,
                  rgba(255, 255, 255, 0.5) 50px,
                  rgba(255, 255, 255, 0.5) 54px
                )
              `,
              backgroundPosition: "0 50%",
            }}
          ></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top section */}
          <div className="flex justify-between items-center">
            <LanguageSwitcher />
            <div className="flex items-center space-x-2 text-white/60 text-sm">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span>{t("forgotPassword.branding.secureRecovery")}</span>
            </div>
          </div>

          {/* Center content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="max-w-lg text-center">
              {/* Lock icon with key */}
              <div className="mb-12 flex justify-center">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-white blur-2xl opacity-10 rounded-full scale-150"></div>

                  {/* Lock and key illustration */}
                  <svg
                    className="w-48 h-48 text-white relative z-10"
                    viewBox="0 0 200 200"
                    fill="none"
                    stroke="currentColor"
                  >
                    {/* Lock body */}
                    <rect
                      x="65"
                      y="95"
                      width="70"
                      height="80"
                      rx="8"
                      fill="currentColor"
                      opacity="0.9"
                      strokeWidth="2"
                    />

                    {/* Lock shackle */}
                    <path
                      d="M 75 95 L 75 75 Q 75 55, 100 55 Q 125 55, 125 75 L 125 95"
                      fill="none"
                      strokeWidth="8"
                      opacity="0.8"
                      strokeLinecap="round"
                    />

                    {/* Keyhole */}
                    <circle
                      cx="100"
                      cy="125"
                      r="8"
                      fill="black"
                      opacity="0.3"
                    />
                    <rect
                      x="96"
                      y="125"
                      width="8"
                      height="20"
                      rx="2"
                      fill="black"
                      opacity="0.3"
                    />

                    {/* Key */}
                    <g transform="translate(140, 120) rotate(25)" opacity="0.7">
                      <circle
                        cx="0"
                        cy="0"
                        r="8"
                        fill="currentColor"
                        strokeWidth="2"
                      />
                      <rect
                        x="8"
                        y="-3"
                        width="35"
                        height="6"
                        rx="2"
                        fill="currentColor"
                        strokeWidth="1"
                      />
                      <rect
                        x="38"
                        y="-6"
                        width="3"
                        height="5"
                        fill="currentColor"
                      />
                      <rect
                        x="43"
                        y="-6"
                        width="3"
                        height="5"
                        fill="currentColor"
                      />
                    </g>

                    {/* Sparkles */}
                    <circle
                      cx="50"
                      cy="70"
                      r="2"
                      fill="currentColor"
                      opacity="0.6"
                      className="animate-pulse"
                    />
                    <circle
                      cx="150"
                      cy="90"
                      r="2"
                      fill="currentColor"
                      opacity="0.6"
                      className="animate-pulse"
                      style={{ animationDelay: "0.5s" }}
                    />
                    <circle
                      cx="140"
                      cy="160"
                      r="2"
                      fill="currentColor"
                      opacity="0.6"
                      className="animate-pulse"
                      style={{ animationDelay: "1s" }}
                    />
                  </svg>
                </div>
              </div>

              {/* Headline */}
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {t("forgotPassword.branding.headline")}
              </h1>

              {/* Tagline */}
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                {t("forgotPassword.branding.description")}
              </p>

              {/* Features */}
              <div className="flex flex-col space-y-4 max-w-sm mx-auto text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {t("forgotPassword.branding.step1")}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {t("forgotPassword.branding.step1Desc")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {t("forgotPassword.branding.step2")}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {t("forgotPassword.branding.step2Desc")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {t("forgotPassword.branding.step3")}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {t("forgotPassword.branding.step3Desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Auto Shkolla
            </div>
            <div className="text-gray-500 text-sm">
              {t("forgotPassword.branding.developedBy")}{" "}
              <a
                href="https://www.ardbraha.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:underline transition-all duration-200 font-medium"
              >
                Ard Braha
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes shake-smooth {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-4px);
          }
          75% {
            transform: translateX(4px);
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-shake-smooth {
          animation: shake-smooth 0.5s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default ForgotPassword;
